// Procedural course generator: floating islands joined by obby connectors,
// brain gates, secrets, scenery and a themed landmark.
import { B, BLOCKS } from './blocks.js';
import { RNG, valueNoise2, clamp } from './util.js';

export class Gen {
  constructor(world, theme, rng) {
    this.w = world;
    this.t = theme;
    this.r = rng;
    this.ents = [];
    this.path = [];
    this.islands = [];
    this.minY = 999;
    this.reserved = []; // xz boxes where scenery must not go
    this.seed = rng.int(1, 99999);
  }

  set(x, y, z, id) {
    this.w.set(x, y, z, id);
    if (id && y < this.minY) this.minY = y;
  }
  get(x, y, z) {
    return this.w.get(x, y, z);
  }
  ent(e) {
    this.ents.push(e);
    return e;
  }
  coin(x, y, z) {
    this.ents.push({ type: 'coin', x, y, z });
  }
  reserve(x0, z0, x1, z1) {
    this.reserved.push([Math.min(x0, x1), Math.min(z0, z1), Math.max(x0, x1), Math.max(z0, z1)]);
  }
  isReserved(x, z, pad = 0) {
    for (const [a, b, c, d] of this.reserved) if (x >= a - pad && x <= c + pad && z >= b - pad && z <= d + pad) return true;
    return false;
  }

  // ---------- islands ----------
  island(cx, cy, cz, rx, rz, o = {}) {
    const t = this.t, r = this.r;
    const surface = o.surface ?? t.surface, sub = o.sub ?? t.sub, deep = o.deep ?? t.deep;
    const maxDepth = o.depth ?? Math.max(4, Math.round((rx + rz) * 0.85));
    const tops = [];
    const sd = this.seed + cx * 7 + cz * 13;
    for (let x = cx - rx - 1; x <= cx + rx + 1; x++)
      for (let z = cz - rz - 1; z <= cz + rz + 1; z++) {
        const nx = (x - cx) / (rx + 0.5), nz = (z - cz) / (rz + 0.5);
        const d = nx * nx + nz * nz;
        const n = valueNoise2(x * 0.45, z * 0.45, sd);
        if (d > 1.0 - (n - 0.5) * 0.35) continue;
        const edge = 1 - Math.sqrt(Math.min(1, d));
        const depth = Math.max(1, Math.floor(1 + edge * maxDepth * (0.75 + n * 0.5) + valueNoise2(x * 0.9, z * 0.9, sd + 5) * 2));
        for (let k = 0; k < depth; k++) {
          const y = cy - k;
          let id = k === 0 ? surface : k <= 2 ? sub : deep;
          if (k > 2 && t.ores && r.chance(0.05)) id = r.pick(t.ores);
          this.set(x, y, z, id);
        }
        tops.push({ x, z, edge });
      }
    const isl = { x: cx, y: cy, z: cz, rx, rz, tops, front: cz + rz, back: cz - rz };
    if (!o.noLane) this.lane(cx, cy, cz - rz - 1, cz + rz + 1, surface, sub);
    return isl;
  }

  // make sure there is a walkable 3-wide strip through an island
  lane(x, y, z0, z1, surface, sub, w = 1) {
    for (let z = z0; z <= z1; z++)
      for (let dx = -w; dx <= w; dx++) {
        if (this.get(x + dx, y, z) === 0 && Math.abs(dx) <= w && z > z0 && z < z1) this.set(x + dx, y, z, surface);
        if (this.get(x + dx, y - 1, z) === 0 && this.get(x + dx, y, z) !== 0) this.set(x + dx, y - 1, z, sub);
        // clear anything standing on the lane
        for (let k = 1; k <= 3; k++) if (this.get(x + dx, y + k, z) !== 0 && z > z0 && z < z1) this.set(x + dx, y + k, z, 0);
      }
  }

  slab(x0, z0, x1, z1, y, id, under = null, thick = 1) {
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++)
      for (let z = Math.min(z0, z1); z <= Math.max(z0, z1); z++) {
        this.set(x, y, z, id);
        for (let k = 1; k < thick; k++) this.set(x, y - k, z, under ?? id);
      }
  }

  // ---------- decoration ----------
  decorate(isl, { trees = true, plants = true, avoidLane = true } = {}) {
    const t = this.t, r = this.r;
    for (const p of isl.tops) {
      const top = isl.y;
      if (this.get(p.x, top, p.z) === 0 || this.get(p.x, top + 1, p.z) !== 0) continue;
      const onLane = avoidLane && Math.abs(p.x - isl.x) <= 1;
      if (onLane || this.isReserved(p.x, p.z)) continue;
      if (trees && p.edge > 0.25 && Math.abs(p.x - isl.x) >= 3 && !this.isReserved(p.x, p.z, 2) && r.chance(t.treeChance)) {
        if (this.tree(p.x, top + 1, p.z, t.tree)) this.reserve(p.x - 2, p.z - 2, p.x + 2, p.z + 2);
        continue;
      }
      if (t.cactusChance && Math.abs(p.x - isl.x) >= 2 && r.chance(t.cactusChance)) {
        const h = r.int(2, 4);
        for (let k = 1; k <= h; k++) this.set(p.x, top + k, p.z, B.CACTUS);
        continue;
      }
      if (t.pumpkins && r.chance(0.012)) {
        this.set(p.x, top + 1, p.z, r.chance(0.5) ? B.JACK_O : B.PUMPKIN);
        continue;
      }
      if (plants && r.chance(t.plantChance)) this.set(p.x, top + 1, p.z, r.pick(t.plants));
    }
  }

  tree(x, y, z, kind) {
    const r = this.r;
    // need some air above
    for (let k = 0; k < 5; k++) if (this.get(x, y + k, z) !== 0) return false;
    if (kind === 'oak' || kind === 'birch') {
      const log = kind === 'oak' ? (r.chance(0.2) ? B.BIRCH_LOG : B.LOG) : B.BIRCH_LOG;
      const leaves = log === B.BIRCH_LOG ? B.BIRCH_LEAVES : B.LEAVES;
      const h = r.int(4, 6);
      for (let k = 0; k < h; k++) this.set(x, y + k, z, log);
      const top = y + h;
      for (let ly = top - 2; ly <= top + 1; ly++) {
        const rad = ly >= top ? 1 : 2;
        for (let dx = -rad; dx <= rad; dx++)
          for (let dz = -rad; dz <= rad; dz++) {
            if (Math.abs(dx) === rad && Math.abs(dz) === rad && (ly >= top || r.chance(0.5))) continue;
            if (ly === top + 1 && Math.abs(dx) + Math.abs(dz) > 1) continue;
            if (this.get(x + dx, ly, z + dz) === 0) this.set(x + dx, ly, z + dz, leaves);
          }
      }
      return true;
    }
    if (kind === 'pine') {
      const h = r.int(6, 9);
      for (let k = 0; k < h; k++) this.set(x, y + k, z, B.PINE_LOG);
      const leaves = this.t.surface === B.SNOW_GRASS ? B.SNOW_PINE_LEAVES : B.PINE_LEAVES;
      let rad = 2;
      for (let ly = y + h; ly >= y + 2; ly--) {
        const rr = ly === y + h ? 0 : ly > y + h - 2 ? 1 : (y + h - ly) % 2 === 0 ? 2 : 1;
        for (let dx = -rr; dx <= rr; dx++)
          for (let dz = -rr; dz <= rr; dz++) {
            if (rr === 2 && Math.abs(dx) === 2 && Math.abs(dz) === 2) continue;
            if (this.get(x + dx, ly, z + dz) === 0) this.set(x + dx, ly, z + dz, leaves);
          }
      }
      this.set(x, y + h, z, leaves);
      this.set(x, y + h + 1, z, leaves);
      return rad > 0;
    }
    if (kind === 'palm') {
      const h = r.int(5, 7);
      const dir = r.pick([[1, 0], [-1, 0], [0, 1], [0, -1]]);
      let tx = x, tz = z;
      for (let k = 0; k < h; k++) {
        if (k === Math.floor(h / 2)) {
          tx += dir[0];
          tz += dir[1];
        }
        this.set(tx, y + k, tz, B.LOG);
      }
      const ty = y + h;
      this.set(tx, ty, tz, B.LEAVES);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        this.set(tx + dx, ty, tz + dz, B.LEAVES);
        this.set(tx + dx * 2, ty, tz + dz * 2, B.LEAVES);
        this.set(tx + dx * 3, ty - 1, tz + dz * 3, B.LEAVES);
      }
      for (const [dx, dz] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) this.set(tx + dx, ty + 1, tz + dz, B.LEAVES);
      this.set(tx, ty + 1, tz, B.LEAVES);
      return true;
    }
    if (kind === 'crystal') {
      const h = r.int(3, 6);
      const mat = this.t.surface === B.VOID_STONE ? B.CRYSTAL : r.chance(0.5) ? B.CRYSTAL : B.GLOWSTONE;
      for (let k = 0; k < h; k++) this.set(x, y + k, z, mat);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (r.chance(0.6)) for (let k = 0; k < r.int(1, h - 1); k++) this.set(x + dx, y + k, z + dz, mat);
      return true;
    }
    return false;
  }

  // ---------- helpers for course pieces ----------
  coinArc(ax, ay, az, bx, by, bz, n, lift = 1.6) {
    for (let i = 1; i <= n; i++) {
      const t = i / (n + 1);
      const x = ax + (bx - ax) * t, z = az + (bz - az) * t;
      const y = ay + (by - ay) * t + Math.sin(t * Math.PI) * lift;
      this.coin(x + 0.5, y + 1.6, z + 0.5);
    }
  }
  coinLine(x, y, z0, z1, step = 2) {
    for (let z = z0; z <= z1; z += step) this.coin(x + 0.5, y + 1.6, z + 0.5);
  }
  coinRing(cx, cy, cz, rad = 2, n = 8) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      this.coin(cx + 0.5 + Math.cos(a) * rad, cy + 1.6, cz + 0.5 + Math.sin(a) * rad);
    }
  }
  railing(x, y, z0, z1, id = B.LOG) {
    for (let z = z0; z <= z1; z++) if (z % 3 === 0) this.set(x, y + 1, z, id);
  }
}

// --------------------------------------------------------------------------------------
// The course
// --------------------------------------------------------------------------------------

const TUTORIAL = [
  { conn: 'bridge', len: 6, island: { r: 4, coins: 'ring' } },
  { conn: 'gap', gap: 2, island: { r: 3, coins: 'line' } },
  { conn: 'stones', stones: 3, island: { r: 5, checkpoint: true, npc: { kind: 'builder', text: 'Nice jumping, {name}! 🪙 Coins buy cool skins and swords in your <b>Locker</b>!' } } },
  { conn: 'gate', gap: 6, needed: 1, island: { r: 5, lucky: true, npc: { kind: 'professor', text: 'See that <b>Lucky Block</b>? Hit it with your sword for a surprise! (Click / E / ⚔️)' } } },
  { conn: 'bounce', island: { r: 6, mobs: 1, secret: true, npc: { kind: 'builder', text: 'Uh-oh, a slime! Bonk it with your sword or <b>stomp</b> on its head! 👟' } } },
  { conn: 'drop', gap: 2, island: { r: 4, coins: 'ring', gem: true } },
  { conn: 'stairs', island: { r: 5, checkpoint: true, brain: true, npc: { kind: 'professor', text: 'Hit purple <b>Brain Blocks</b> with your sword, then solve the puzzle for bonus 💎 gems!' } } },
  { conn: 'gate', gap: 6, needed: 1, island: { r: 4, coins: 'line' } },
  { conn: 'gap', gap: 2, island: { r: 4, coins: 'ring', heart: true } },
];

function makePlan(d, theme, r) {
  const n = 9 + Math.floor(d / 3);
  const plan = [];
  const gateCount = d === 0 ? 2 : 3;
  const needed = d === 0 ? 1 : d < 3 ? 2 : d < 9 ? 2 : 3;
  const gateAt = new Set();
  for (let g = 0; g < gateCount; g++) gateAt.add(Math.round(((g + 1) / (gateCount + 1)) * (n - 1)) + (g === gateCount - 1 ? 1 : 0));
  const pool = [
    ['gap', 3], ['stones', 3], ['bridge', d < 6 ? 1.6 : 0.8], ['stairs', 1.4], ['drop', 1.1], ['bounce', 1.5],
  ];
  if (d >= 1) pool.push(['moving', 1.6]);
  if (d >= 2) pool.push(['crumble', 1.3]);
  if (d >= 3) pool.push(['sideMovers', 1.3]);
  if (d >= 5) pool.push(['spinner', 1.2]);
  if (theme.ice) pool.push(['ice', 2.2]);
  if (theme.lavaSea) pool.push(['lavaRiver', 2.4]);
  const secretAt = r.int(Math.floor(n * 0.3), Math.floor(n * 0.6));
  const luckyAt = new Set([r.int(1, Math.floor(n / 2)), r.int(Math.floor(n / 2) + 1, n - 2)]);
  const brainAt = new Set();
  for (let tries = 0; brainAt.size < (d >= 6 ? 2 : 1) && tries < 20; tries++) {
    const i = r.int(2, n - 2);
    if (!luckyAt.has(i)) brainAt.add(i); // never on the same island as a Lucky Block
  }
  const gemAt = new Set([r.int(1, n - 1), r.int(1, n - 1), r.int(1, n - 1)]);
  let last = '';
  let sinceCheck = 0;
  for (let i = 0; i < n; i++) {
    const step = { island: {} };
    if (gateAt.has(i)) {
      step.conn = 'gate';
      step.needed = needed;
      step.gap = r.int(5, 7);
    } else {
      let c;
      let tries = 0;
      do {
        c = r.weighted(pool);
        tries++;
      } while ((c === last || (c === 'spinner' && i < 2)) && tries < 10);
      step.conn = c;
      last = c;
    }
    const isl = step.island;
    isl.r = r.int(3, 5);
    sinceCheck++;
    if (step.conn === 'gate' || sinceCheck >= 3) {
      isl.checkpoint = true;
      sinceCheck = 0;
      isl.r = Math.max(isl.r, 4);
    }
    const mobChance = 0.25 + d * 0.035;
    if (i > 0 && r.chance(mobChance)) {
      isl.mobs = 1 + (d >= 5 && r.chance(0.5) ? 1 : 0) + (d >= 10 && r.chance(0.4) ? 1 : 0);
      isl.r = Math.max(isl.r, 5);
    }
    if (luckyAt.has(i)) isl.lucky = true;
    if (brainAt.has(i)) isl.brain = true;
    if (gemAt.has(i)) step.gem = true;
    if (i === secretAt) {
      isl.secret = true;
      isl.r = Math.max(isl.r, 5);
    }
    if (i === Math.floor(n / 2) && d >= 2) isl.heart = true;
    if (i === Math.floor(n * 0.4) && d >= 1) isl.powerup = r.pick(['magnet', 'speed', 'jump']);
    isl.coins = r.pick(['line', 'ring', 'ring', 'line', 'zig']);
    plan.push(step);
  }
  return plan;
}

// NPC lines say {name}: each NPC fills in whoever is playing (entities.js).
const GATE_LINES = [
  'Oh no, {name}! The Glitch King smashed this bridge! Solve the puzzle to rebuild it! 🧱',
  'This bridge is scrambled! Only a Brain Builder can fix it — that’s you, {name}! 🧠',
  'Quick, {name}! Answer correctly and the blocks will fly back into place! ⚡',
  'The Glitch King hid the bridge blocks in a puzzle. Can you crack it, {name}?',
];

export function generateCourse(world, theme, opts) {
  const r = new RNG(opts.seed);
  const g = new Gen(world, theme, r);
  const d = opts.difficulty;
  const plan = opts.tutorial ? TUTORIAL : makePlan(d, theme, r);
  const gapMin = 1 + Math.floor(d / 6);
  const gapMax = Math.min(2 + Math.floor(d / 3), opts.doubleJump ? 5 : 3);

  // --- start island ---
  let cur = g.island(0, 40, 0, 6, 6);
  g.reserve(-2, -6, 2, 6);
  g.path.push({ x: 0.5, y: 41, z: 0.5 });
  const spawn = { x: 0.5, y: 41, z: -2.5, yaw: 0 };
  g.ent({ type: 'checkpoint', x: 0, y: 41, z: -3, start: true });
  g.ent({
    type: 'npc', role: 'welcome', kind: 'professor', x: 3.5, y: 41, z: 4.5, yaw: -2.8,
    text: opts.tutorial && !opts.quietGuides
      ? 'Hi {name}! I’m <b>Professor Pixel</b>! 👋 Move with <b>WASD</b> / joystick and jump with <b>SPACE</b> / ⤒. Reach the portal at the end!'
      : opts.introText || `Welcome to <b>${opts.levelName}</b>, {name}! Follow the coins to the portal. ⭐ Look for a secret!`,
  });
  g.decorate(cur);
  g.coinLine(0, 40, 1, 5, 2);

  let gates = 0;
  let stepIdx = 0;
  for (const step of plan) {
    stepIdx++;
    const isl = step.island;
    const nr = isl.r;
    const A = { x: cur.x, y: cur.y, z: cur.front };
    let gap = 2, dy = 0, nx = cur.x;
    const straight = ['bridge', 'gate', 'crumble', 'ice', 'lavaRiver', 'spinner', 'stairs', 'sideMovers', 'moving'].includes(step.conn);
    switch (step.conn) {
      case 'gap':
        gap = step.gap ?? r.int(gapMin, gapMax);
        dy = r.int(-1, 1);
        break;
      case 'stones':
        // stones are spaced for a normal running jump (~4 blocks centre to centre)
        step.n = step.stones ?? r.int(2, 4);
        gap = step.n * 4 + 2;
        dy = r.int(-1, 1);
        break;
      case 'bridge':
        gap = step.len ?? r.int(5, 9);
        break;
      case 'stairs':
        gap = 6 + r.int(0, 2);
        dy = 3 + r.int(0, 1);
        break;
      case 'drop':
        gap = step.gap ?? r.int(2, 3);
        dy = -r.int(3, 6);
        break;
      case 'bounce':
        gap = r.int(2, 3);
        dy = r.int(4, 6);
        break;
      case 'moving':
        gap = r.int(7, 10);
        break;
      case 'sideMovers':
        gap = 10;
        break;
      case 'crumble':
        gap = r.int(5, 8);
        break;
      case 'gate':
        gap = step.gap ?? 6;
        break;
      case 'ice':
        gap = r.int(8, 12);
        break;
      case 'lavaRiver':
        step.n = r.int(2, 3);
        gap = step.n * 4 + 2;
        break;
      case 'spinner':
        gap = 9;
        break;
    }
    // keep the course in a comfy height band
    let ny = cur.y + dy;
    if (ny > 58) ny = cur.y - Math.abs(dy);
    if (ny < 30) ny = cur.y + Math.abs(dy);
    if (step.conn === 'bounce' && ny <= cur.y) ny = cur.y + 5;
    if (step.conn === 'stairs' && ny <= cur.y) ny = cur.y + 3;
    if (step.conn === 'drop' && ny >= cur.y) ny = cur.y - 3;
    if (!straight) nx = clamp(cur.x + r.int(-4, 4), -7, 7);
    const nz = cur.front + gap + nr + 1;
    const Bp = { x: nx, y: ny, z: nz - nr };

    // ---------- connectors ----------
    switch (step.conn) {
      case 'gap':
        g.coinArc(A.x, A.y, A.z, Bp.x, Bp.y, Bp.z, Math.max(1, gap), 1.8);
        if (step.gem) g.ent({ type: 'gem', x: (A.x + Bp.x) / 2 + 0.5, y: Math.max(A.y, Bp.y) + 3.4, z: (A.z + Bp.z) / 2 + 0.5 });
        break;
      case 'stones': {
        const n = step.n;
        let sy = A.y;
        for (let i = 1; i <= n; i++) {
          const t = i / (n + 1);
          const sz = A.z + 3 + (i - 1) * 4;
          const sx = Math.round(A.x + (Bp.x - A.x) * t) - (r.chance(0.5) ? 1 : 0);
          // never more than one block up between stones
          const want = Math.round(A.y + (Bp.y - A.y) * t) + (d >= 4 ? r.int(-1, 1) : 0);
          sy = Math.min(sy + 1, want);
          const block = r.pick(theme.pad);
          g.slab(sx, sz, sx + 1, sz + 1, sy, block, theme.stone, 2);
          g.coin(sx + 1, sy + 1.6, sz + 1);
          if (step.gem && i === n) g.ent({ type: 'gem', x: sx + 1, y: sy + 3.4, z: sz + 1 });
        }
        if (ny > sy + 1) ny = sy + 1;
        break;
      }
      case 'bridge': {
        for (let z = A.z + 1; z < Bp.z; z++) {
          for (let dx = -1; dx <= 1; dx++) g.set(A.x + dx, A.y, z, theme.bridge);
          if ((z - A.z) % 4 === 0) {
            g.set(A.x - 2, A.y, z, B.LOG);
            g.set(A.x + 2, A.y, z, B.LOG);
            g.set(A.x - 2, A.y + 1, z, B.LOG);
            g.set(A.x + 2, A.y + 1, z, B.LOG);
            if (theme.lavaSea || theme.mobs.includes('flyer')) g.set(A.x + 2, A.y + 2, z, B.LAMP);
          }
        }
        g.coinLine(A.x, A.y, A.z + 1, Bp.z - 1, 2);
        break;
      }
      case 'stairs': {
        const steps = ny - cur.y;
        const len = gap;
        for (let s = 0; s < steps; s++) {
          const z0 = A.z + 1 + Math.floor((s * len) / steps);
          const z1 = A.z + Math.floor(((s + 1) * len) / steps);
          g.slab(A.x - 1, z0, A.x + 1, Math.min(z1, Bp.z - 1), cur.y + s + 1, s % 2 ? theme.bridge : theme.stone, theme.stone, 2);
          g.coin(A.x + 0.5, cur.y + s + 2.6, z0 + 0.5);
        }
        break;
      }
      case 'drop':
        g.coinArc(A.x, A.y, A.z, Bp.x, Bp.y, Bp.z, gap + 2, 1.0);
        break;
      case 'bounce': {
        for (let dx = -1; dx <= 1; dx++) g.set(A.x + dx, A.y, A.z - 1, B.BOUNCE);
        g.set(A.x, A.y, A.z, B.BOUNCE);
        for (let i = 1; i <= 5; i++) {
          const t = i / 6;
          g.coin(A.x + 0.5 + (Bp.x - A.x) * t, A.y + 1.6 + Math.sin(t * Math.PI * 0.9) * (ny - A.y + 2.5), A.z - 0.5 + (Bp.z - A.z + 1) * t);
        }
        g.ent({ type: 'sign', x: A.x - 2.5, y: A.y + 1, z: A.z - 2, yaw: Math.PI, text: 'BOUNCE!' });
        break;
      }
      case 'moving': {
        const block = r.pick(theme.pad);
        g.ent({
          type: 'moving', w: 3, d: 3, block,
          from: { x: A.x - 1, y: A.y, z: A.z + 1 },
          to: { x: A.x - 1, y: A.y, z: Bp.z - 3 },
          period: 4 + gap * 0.25,
        });
        g.coinLine(A.x, A.y + 0.5, A.z + 2, Bp.z - 2, 2);
        break;
      }
      case 'sideMovers': {
        const block = r.pick(theme.pad);
        const zs = [A.z + 3, A.z + 7];
        zs.forEach((z, i) => {
          g.ent({
            type: 'moving', w: 2, d: 2, block,
            from: { x: A.x - 4, y: A.y, z },
            to: { x: A.x + 3, y: A.y, z },
            period: 3.6 + i * 0.6, phase: i * 0.5,
          });
          g.coin(A.x + 0.5, A.y + 1.8, z + 1);
        });
        break;
      }
      case 'crumble': {
        const w = d < 6 ? 1 : 0;
        for (let z = A.z + 1; z < Bp.z; z++)
          for (let dx = -w; dx <= w; dx += w || 1) g.ent({ type: 'crumble', x: A.x + dx, y: A.y, z, block: theme.bridge });
        g.coinLine(A.x, A.y, A.z + 2, Bp.z - 2, 2);
        break;
      }
      case 'ice': {
        for (let z = A.z + 1; z < Bp.z; z++) {
          for (let dx = -1; dx <= 1; dx++) g.set(A.x + dx, A.y, z, B.ICE);
          g.set(A.x - 2, A.y, z, B.PACKED_ICE);
          g.set(A.x + 2, A.y, z, B.PACKED_ICE);
          g.set(A.x - 2, A.y + 1, z, B.PACKED_ICE);
          g.set(A.x + 2, A.y + 1, z, B.PACKED_ICE);
        }
        g.coinLine(A.x, A.y, A.z + 1, Bp.z - 1, 1);
        g.ent({ type: 'sign', x: A.x + 2.5, y: A.y + 1, z: A.z - 2, yaw: Math.PI, text: 'SLIPPERY!' });
        break;
      }
      case 'lavaRiver': {
        for (let z = A.z + 1; z < Bp.z; z++)
          for (let dx = -3; dx <= 3; dx++) {
            g.set(A.x + dx, A.y - 1, z, B.LAVA);
            g.set(A.x + dx, A.y - 2, z, B.MAGMA_ROCK);
            g.set(A.x + dx, A.y - 3, z, B.BASALT);
          }
        for (let z = A.z + 1; z < Bp.z; z++) {
          g.set(A.x - 4, A.y - 1, z, B.MAGMA_ROCK);
          g.set(A.x + 4, A.y - 1, z, B.MAGMA_ROCK);
        }
        for (let i = 0; i < step.n; i++) {
          const z = A.z + 3 + i * 4;
          const sx = A.x - (i % 2 === 0 ? 1 : 0);
          for (let dz = 0; dz <= 1; dz++)
            for (let dx = 0; dx <= 1; dx++) {
              g.set(sx + dx, A.y, z + dz, B.BASALT);
              g.set(sx + dx, A.y - 1, z + dz, B.BASALT);
            }
          g.coin(sx + 1, A.y + 1.6, z + 1);
        }
        break;
      }
      case 'spinner': {
        for (let z = A.z + 1; z < Bp.z; z++) for (let dx = -1; dx <= 1; dx++) g.set(A.x + dx, A.y, z, theme.bridge);
        const mz = Math.floor((A.z + Bp.z) / 2);
        g.ent({ type: 'spinner', x: A.x + 0.5, y: A.y + 1, z: mz + 0.5, len: 7, speed: 1.3 + d * 0.05 });
        g.set(A.x, A.y + 1, mz, B.OBSIDIAN);
        g.coinLine(A.x, A.y, A.z + 1, mz - 2, 2);
        break;
      }
      case 'gate': {
        gates++;
        const blocks = [];
        for (let z = A.z + 1; z < Bp.z; z++) for (let dx = -1; dx <= 1; dx++) blocks.push({ x: A.x + dx, y: A.y, z, id: theme.bridge });
        g.ent({
          type: 'gate', blocks, needed: step.needed ?? 1,
          trigger: { x: A.x + 0.5, y: A.y + 1, z: A.z - 0.5 },
          title: 'Rebuild the bridge!',
        });
        g.ent({
          type: 'npc', kind: gates % 2 ? 'professor' : 'builder', x: A.x + 3.5, y: A.y + 1, z: A.z - 2.5, yaw: Math.PI + 0.6,
          text: opts.tutorial && gates === 1 ? 'The Glitch King smashed this bridge! Walk up to the glowing <b>Brain Block</b> and press <b>E</b> / ⚡ to solve the puzzle and rebuild it!' : r.pick(GATE_LINES),
        });
        break;
      }
    }

    // ---------- next island ----------
    const next = g.island(nx, ny, nz, nr, nr);
    g.path.push({ x: nx + 0.5, y: ny + 1, z: nz + 0.5 });
    g.reserve(nx - 1, nz - nr, nx + 1, nz + nr);
    const top = ny + 1;
    // contents
    if (isl.checkpoint) {
      g.ent({ type: 'checkpoint', x: nx, y: top, z: nz });
      g.reserve(nx - 2, nz - 1, nx + 2, nz + 1);
    }
    if (isl.lucky) {
      const lx = nx + (r.chance(0.5) ? -2 : 2);
      g.ent({ type: 'lucky', x: lx + 0.5, y: top + 0.5, z: nz + 1.5 });
      g.reserve(lx - 1, nz, lx + 1, nz + 2);
    }
    if (isl.brain) {
      const bx = nx + (r.chance(0.5) ? -2 : 2);
      g.ent({ type: 'brainblock', x: bx + 0.5, y: top + 0.5, z: nz - 1.5 });
      g.reserve(bx - 1, nz - 3, bx + 1, nz - 1);
    }
    if (isl.mobs) {
      const kinds = theme.mobs;
      for (let m = 0; m < isl.mobs; m++) {
        const kind = opts.tutorial ? 'slime' : kinds[Math.min(kinds.length - 1, Math.floor(r.next() * Math.min(kinds.length, 1 + Math.floor(d / 3))))];
        g.ent({ type: 'mob', kind, x: nx + 0.5 + r.int(-2, 2), y: kind === 'flyer' ? top + 2.5 : top, z: nz + 0.5 + r.int(-1, 2) });
      }
    }
    if (isl.heart) g.ent({ type: 'heart', x: nx + 0.5 + (r.chance(0.5) ? -2 : 2), y: top + 0.8, z: nz + 0.5 });
    if (isl.powerup) g.ent({ type: 'powerup', kind: isl.powerup, x: nx + 0.5, y: top + 0.9, z: nz - nr + 2.5 });
    if (isl.gem) g.ent({ type: 'gem', x: nx + 0.5 + (r.chance(0.5) ? -3 : 3), y: top + 0.9, z: nz + 0.5 });
    if (isl.npc) {
      g.ent({ type: 'npc', kind: isl.npc.kind, x: nx - 2.5, y: top, z: nz - nr + 2.5, yaw: Math.PI - 0.5, text: isl.npc.text });
      g.reserve(nx - 4, nz - nr + 1, nx - 1, nz - nr + 4);
    }
    // coins
    if (isl.coins === 'ring') g.coinRing(nx, ny, nz, Math.min(2.5, nr - 1), 8);
    else if (isl.coins === 'zig') {
      let k = 0;
      for (let z = nz - nr + 1; z <= nz + nr - 1; z += 2) g.coin(nx + 0.5 + (k++ % 2 ? -1.2 : 1.2), ny + 1.6, z + 0.5);
    }
    else g.coinLine(nx, ny, nz - nr + 1, nz + nr - 1, 2);
    // secret branch
    if (isl.secret) buildSecret(g, next, theme, r);
    g.decorate(next);
    cur = next;
  }

  // ---------- final island ----------
  const gapEnd = r.int(2, 3);
  const end = g.island(cur.x, cur.y, cur.front + gapEnd + 8, 7, 7);
  g.coinArc(cur.x, cur.y, cur.front, cur.x, cur.y, end.back, gapEnd, 1.8);
  g.path.push({ x: end.x + 0.5, y: end.y + 1, z: end.z + 0.5 });
  const ex = end.x, ey = end.y, ezc = end.z;
  // portal frame (opening 3 wide x 4 tall) facing -z
  const pz = ezc + 4;
  for (let x = ex - 2; x <= ex + 2; x++) {
    g.set(x, ey + 1, pz, B.OBSIDIAN);
    g.set(x, ey + 6, pz, B.OBSIDIAN);
  }
  for (let y = ey + 1; y <= ey + 6; y++) {
    g.set(ex - 2, y, pz, B.OBSIDIAN);
    g.set(ex + 2, y, pz, B.OBSIDIAN);
  }
  for (const x of [ex - 3, ex + 3]) {
    g.set(x, ey + 1, pz, B.GLOWSTONE);
    g.set(x, ey + 2, pz, B.OBSIDIAN);
  }
  // clear inside
  for (let x = ex - 1; x <= ex + 1; x++) for (let y = ey + 2; y <= ey + 5; y++) g.set(x, y, pz, 0);
  // golden path to the portal
  for (let z = end.back + 1; z < pz; z++) for (let dx = -1; dx <= 1; dx++) g.set(ex + dx, ey, z, dx === 0 ? B.GOLD_BLOCK : B.MARBLE);
  g.ent({ type: 'portal', x: ex + 0.5, y: ey + 2, z: pz + 0.5 });
  g.ent({ type: 'chest', x: ex + 2.5, y: ey + 1, z: ezc + 0.5 });
  g.ent({ type: 'npc', kind: 'professor', x: ex - 2.5, y: ey + 1, z: ezc - 2.5, yaw: Math.PI - 0.4, text: 'You made it, {name}! 🎉 Jump into the <b>portal</b> to finish the level!' });
  g.reserve(ex - 4, end.back, ex + 4, pz + 1);
  g.decorate(end);
  g.coinRing(ex, ey, ezc - 2, 2.5, 10);
  const endZ = pz;

  scenery(g, theme, r, -20, endZ + 40);
  fixCoins(g);
  const killY = g.minY - 6;
  // Filter after generation so scenery, obstacles, loot and RNG stay identical.
  const ents = opts.tutorial && !opts.quietGuides ? g.ents : g.ents.filter(e => e.type !== 'npc' || e.role === 'welcome');
  return { spawn, ents, path: g.path, killY, endZ, gates };
}

// Lift any coin that ended up inside a block or an entity block (lucky block, chest…)
function fixCoins(g) {
  const occupied = new Set();
  for (const e of g.ents) {
    if (e.type === 'lucky' || e.type === 'brainblock') occupied.add(`${Math.floor(e.x)},${Math.floor(e.y - 0.5)},${Math.floor(e.z)}`);
    if (e.type === 'chest' || e.type === 'checkpoint') occupied.add(`${Math.floor(e.x)},${Math.floor(e.y)},${Math.floor(e.z)}`);
  }
  const blockedAt = (c) => {
    const x = Math.floor(c.x), y = Math.floor(c.y), z = Math.floor(c.z);
    const id = g.get(x, y, z);
    return (id && BLOCKS[id].solid) || BLOCKS[id]?.cross || occupied.has(`${x},${y},${z}`);
  };
  for (const c of g.ents) {
    if (c.type !== 'coin') continue;
    for (let k = 0; k < 2 && blockedAt(c); k++) c.y += 1;
    if (blockedAt(c)) c.type = 'removed'; // buried in a tree trunk etc.
  }
  g.ents = g.ents.filter((e) => e.type !== 'removed');
}

function buildSecret(g, isl, theme, r) {
  const side = isl.x >= 0 ? -1 : 1;
  const style = r.chance(0.5) ? 'stones' : 'bounce';
  const sx = isl.x + side * (isl.rx + 11);
  const sz = isl.z;
  if (style === 'stones') {
    const sy = isl.y + r.int(0, 2);
    const sec = g.island(sx, sy, sz, 3, 3, { noLane: true, depth: 4 });
    // stones heading sideways
    const n = 4;
    for (let i = 1; i <= n; i++) {
      const t = i / (n + 1);
      const x = Math.round(isl.x + side * isl.rx + (sx - side * 3 - (isl.x + side * isl.rx)) * t);
      const y = Math.round(isl.y + (sy - isl.y) * t);
      g.slab(x, sz, x, sz, y, r.pick(theme.pad), theme.stone, 2);
      g.coin(x + 0.5, y + 1.6, sz + 0.5);
    }
    g.set(sx, sy + 1, sz, B.GOLD_BLOCK);
    g.ent({ type: 'star', x: sx + 0.5, y: sy + 3, z: sz + 0.5 });
    g.coinRing(sx, sy, sz, 2, 6);
    g.decorate(sec, { trees: false });
  } else {
    // bounce pad at the island's side launches Sai up onto a floating cloud
    const bx = isl.x + side * (isl.rx - 1);
    for (let k = 1; k <= 3; k++) g.set(bx, isl.y + k, sz, 0);
    g.set(bx, isl.y, sz, B.BOUNCE);
    g.reserve(bx - 2, sz - 2, bx + 2, sz + 2);
    const cy = isl.y + 6;
    const cx = bx + side * 6;
    // a big fluffy landing cloud where a running bounce comes down
    for (let x = cx - 3; x <= cx + 3; x++) for (let z = sz - 3; z <= sz + 3; z++) if (Math.abs(x - cx) + Math.abs(z - sz) <= 5) g.set(x, cy, z, B.CLOUD);
    g.set(cx, cy + 1, sz, B.GOLD_BLOCK);
    g.ent({ type: 'star', x: cx + 0.5, y: cy + 3, z: sz + 0.5 });
    // coins trace the bounce arc (launch speed 22, gravity 30, running 6.4)
    for (let i = 1; i <= 4; i++) {
      const t = i * 0.14;
      g.coin(bx + 0.5 + side * 6.4 * t, isl.y + 1 + 22 * t - 15 * t * t + 0.9, sz + 0.5);
    }
  }
  g.ent({ type: 'sign', x: isl.x + side * 2 + 0.5, y: isl.y + 1, z: isl.z - 1.5, yaw: Math.PI, text: '👀 SECRET?' });
}

// ---------- scenery: background islands + landmark ----------
function scenery(g, theme, r, z0, z1) {
  const count = Math.round((z1 - z0) / 14);
  const lz = Math.floor((z0 + z1) / 2);
  const lside = r.chance(0.5) ? 1 : -1;
  for (let i = 0; i < count; i++) {
    const side = i % 2 ? 1 : -1;
    const x = side * r.int(36, 78);
    const z = r.int(z0, z1);
    if (Math.hypot(x - lside * 52, z - lz) < 30) continue;
    const y = r.int(22, 64);
    const rad = r.int(3, 8);
    const isl = g.island(x, y, z, rad, rad + r.int(-1, 2), { noLane: true });
    g.decorate(isl, { avoidLane: false });
    if (theme.water && r.chance(0.35)) {
      // waterfall over the edge
      const wx = x + side * -rad, wz = z;
      for (let k = 0; k < r.int(8, 20); k++) g.set(wx - side, y - k, wz, B.WATER);
      g.set(wx, y, wz, B.WATER);
    }
    if (theme.lavaSea && r.chance(0.35)) {
      const lx = x, lz = z;
      for (let k = 0; k < r.int(6, 16); k++) g.set(lx + rad + 1, y - k, lz, B.LAVA);
    }
  }
  landmark(g, theme, r, lside * 52, 36, lz);
}

function landmark(g, theme, r, x, y, z) {
  const kind = theme.landmark;
  if (kind === 'bigTree') {
    const isl = g.island(x, y, z, 11, 11, { noLane: true });
    g.decorate(isl, { trees: false, avoidLane: false });
    for (let k = 1; k <= 26; k++)
      for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) g.set(x + dx, y + k, z + dz, B.LOG);
    for (const [dx, dz] of [[2, 0], [-2, 0], [0, 2], [0, -2]]) for (let k = 1; k <= 3; k++) g.set(x + dx, y + k - 1, z + dz, B.LOG);
    const cy = y + 26;
    for (let dx = -9; dx <= 9; dx++)
      for (let dy = -5; dy <= 6; dy++)
        for (let dz = -9; dz <= 9; dz++) {
          const dd = (dx * dx) / 81 + (dy * dy) / 36 + (dz * dz) / 81;
          if (dd < 1 && valueNoise2(dx * 0.4 + dy, dz * 0.4, 9) > 0.18) g.set(x + dx, cy + dy, z + dz, r.chance(0.04) ? B.GLOWSTONE : B.LEAVES);
        }
  } else if (kind === 'pyramid') {
    const isl = g.island(x, y, z, 14, 14, { noLane: true });
    g.decorate(isl, { trees: false, avoidLane: false });
    for (let k = 0; k < 12; k++) {
      const s = 11 - k;
      for (let dx = -s; dx <= s; dx++)
        for (let dz = -s; dz <= s; dz++) {
          const edge = Math.abs(dx) === s || Math.abs(dz) === s;
          if (!edge && k < 11) continue;
          g.set(x + dx, y + 1 + k, z + dz, (k + dx + dz) % 5 === 0 ? B.CHISELED_SANDSTONE : B.SANDSTONE);
        }
    }
    g.set(x, y + 13, z, B.GOLD_BLOCK);
    for (let k = 1; k <= 3; k++) for (let dx = -1; dx <= 1; dx++) g.set(x + dx, y + k, z - 11, 0);
  } else if (kind === 'iceCastle') {
    const isl = g.island(x, y, z, 12, 12, { noLane: true });
    g.decorate(isl, { avoidLane: false });
    const S = 7;
    for (let dx = -S; dx <= S; dx++)
      for (let dz = -S; dz <= S; dz++) {
        const edge = Math.abs(dx) === S || Math.abs(dz) === S;
        if (!edge) continue;
        for (let k = 1; k <= 8; k++) g.set(x + dx, y + k, z + dz, k === 8 && (dx + dz) % 2 ? 0 : B.PACKED_ICE);
      }
    for (const [cx, cz] of [[-S, -S], [S, -S], [-S, S], [S, S]]) {
      for (let k = 1; k <= 14; k++) for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) g.set(x + cx + dx, y + k, z + cz + dz, k > 12 ? B.SNOW : B.ICE);
      g.set(x + cx, y + 15, z + cz, B.GLOWSTONE);
    }
    for (let k = 1; k <= 20; k++) for (let dx = -2; dx <= 2; dx++) for (let dz = -2; dz <= 2; dz++) if (Math.abs(dx) === 2 || Math.abs(dz) === 2) g.set(x + dx, y + k, z + dz, k > 18 ? B.SNOW : B.PACKED_ICE);
    g.set(x, y + 21, z, B.DIAMOND_BLOCK);
  } else if (kind === 'volcano') {
    for (let dy = 0; dy < 26; dy++) {
      const rad = 16 - dy * 0.5;
      for (let dx = -Math.ceil(rad); dx <= rad; dx++)
        for (let dz = -Math.ceil(rad); dz <= rad; dz++) {
          const dd = Math.hypot(dx, dz);
          if (dd > rad) continue;
          const crater = dy > 20 && dd < rad - 2;
          if (crater) {
            if (dy === 21) g.set(x + dx, y + dy - 10, z + dz, B.LAVA);
            continue;
          }
          g.set(x + dx, y + dy - 10, z + dz, r.chance(0.08) ? B.MAGMA : dy < 4 ? B.BASALT : B.MAGMA_ROCK);
        }
    }
    for (let k = 0; k < 18; k++) g.set(x + 6 - Math.floor(k / 4), y + 13 - k, z - 7 + Math.floor(k / 6), B.LAVA);
  } else if (kind === 'glitchTower') {
    const isl = g.island(x, y, z, 9, 9, { noLane: true });
    g.decorate(isl, { avoidLane: false });
    for (let k = 1; k <= 30; k++) {
      const s = k > 26 ? 4 : 3;
      for (let dx = -s; dx <= s; dx++)
        for (let dz = -s; dz <= s; dz++) {
          if (Math.abs(dx) !== s && Math.abs(dz) !== s) continue;
          const id = r.chance(0.08) ? B.GLITCH : k % 6 === 0 ? B.CRYSTAL : B.VOID_BRICK;
          g.set(x + dx, y + k, z + dz, id);
        }
    }
    for (let i = 0; i < 18; i++) {
      const a = r.next() * Math.PI * 2, rr = r.range(6, 13);
      g.set(Math.round(x + Math.cos(a) * rr), y + r.int(8, 34), Math.round(z + Math.sin(a) * rr), r.pick([B.GLITCH, B.CRYSTAL, B.OBSIDIAN]));
    }
    g.set(x, y + 32, z, B.GLOWSTONE);
  }
}

// ---------- boss arena ----------
export function generateArena(world, theme, kind) {
  const r = new RNG(777 + kind.length);
  const g = new Gen(world, theme, r);
  const R = 15;
  const isl = g.island(0, 40, 0, R, R, { noLane: true, depth: 14 });
  // make the top flat and patterned
  for (let x = -R - 1; x <= R + 1; x++)
    for (let z = -R - 1; z <= R + 1; z++) {
      const d = Math.hypot(x, z);
      if (d > R + 0.3) continue;
      if (g.get(x, 40, z) === 0) {
        g.set(x, 40, z, theme.surface);
        g.set(x, 39, z, theme.sub);
      }
      const ring = Math.floor(d);
      if (ring === 5 || ring === 11) g.set(x, 40, z, theme.stone);
      else if (ring < 2) g.set(x, 40, z, B.GOLD_BLOCK);
      for (let k = 1; k <= 4; k++) g.set(x, 40 + k, z, 0);
    }
  // low perimeter posts with lights (short so they never block the camera)
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const px = Math.round(Math.cos(a) * (R - 0.5)), pz = Math.round(Math.sin(a) * (R - 0.5));
    g.set(px, 41, pz, theme.stone);
    g.set(px, 42, pz, theme.lavaSea ? B.MAGMA : B.GLOWSTONE);
  }
  scenery(g, theme, r, -60, 60);
  return { spawn: { x: 0.5, y: 41, z: -11.5, yaw: 0 }, killY: g.minY - 6, ents: g.ents, path: [{ x: 0.5, y: 46, z: 0.5 }] };
}

// ---------- blitz arena + menu diorama islands ----------
export function generateSmallIsland(world, theme, seed = 5, R = 9) {
  const r = new RNG(seed);
  const g = new Gen(world, theme, r);
  const isl = g.island(0, 40, 0, R, R, { noLane: true, depth: 10 });
  g.decorate(isl, { avoidLane: false });
  return { g, isl };
}

export function generateBlitzArena(world, theme) {
  const r = new RNG(4242);
  const g = new Gen(world, theme, r);
  const R = 10;
  g.island(0, 40, 0, R, R, { noLane: true, depth: 9 });
  for (let x = -R - 1; x <= R + 1; x++)
    for (let z = -R - 1; z <= R + 1; z++) {
      const d = Math.hypot(x, z);
      if (d > R + 0.3) continue;
      g.set(x, 40, z, d < 2 ? B.GOLD_BLOCK : Math.floor(d) === 6 ? theme.stone : theme.surface);
      g.set(x, 39, z, theme.sub);
      for (let k = 1; k <= 4; k++) g.set(x, 40 + k, z, 0);
    }
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const px = Math.round(Math.cos(a) * (R - 0.5)), pz = Math.round(Math.sin(a) * (R - 0.5));
    for (let k = 1; k <= 3; k++) g.set(px, 40 + k, pz, theme.stone);
    g.set(px, 44, pz, B.GLOWSTONE);
  }
  scenery(g, theme, r, -50, 50);
  return { spawn: { x: 0.5, y: 41, z: 0.5, yaw: 0 }, killY: g.minY - 6 };
}
